import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type PageValidationContextValue = {
  hasValidationErrors: boolean;
  setError: (id: string) => void;
  clearError: (id: string) => void;
};

const PageValidationContext = createContext<PageValidationContextValue | null>(
  null
);

export const usePageValidation = (): PageValidationContextValue => {
  const context = useContext(PageValidationContext);
  if (!context) {
    throw new Error(
      'usePageValidation must be used within a PageValidationProvider'
    );
  }
  return context;
};

/**
 * Tracks which form components currently have validation errors, identified by
 * a unique ID per component (not the error messages themselves). The page-level
 * save action can check `hasValidationErrors` to prevent saving when any
 * component has reported an error via `setError(id)` / `clearError(id)`.
 */
export const PageValidationProvider: FC<PropsWithChildren> = ({ children }) => {
  const [errorIds, setErrorIds] = useState<Set<string>>(new Set());

  const setError = useCallback((id: string) => {
    setErrorIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const clearError = useCallback((id: string) => {
    setErrorIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const value = useMemo(
    (): PageValidationContextValue => ({
      hasValidationErrors: errorIds.size > 0,
      setError,
      clearError,
    }),
    [errorIds, setError, clearError]
  );

  return (
    <PageValidationContext.Provider value={value}>
      {children}
    </PageValidationContext.Provider>
  );
};
